import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn } from '@angular/router';
import { MmuUserRoles, UserRole } from '../../auth/models';
import { MmuUserService } from '../../auth/user-service/mmu-user.service';
import { MmuLogger } from '../../logging';


export interface IsAuthorizedGuardOptions {
  whitelist: Array<UserRole>;
  blacklist: Array<UserRole>;
}

const defaultWhitelist = [MmuUserRoles.BASIC, MmuUserRoles.ENGINEER, MmuUserRoles.ADMIN];

type IsAuthorizedArgumentType = UserRole | UserRole[] | Partial<IsAuthorizedGuardOptions>;

function getWhitelist(input: IsAuthorizedArgumentType): UserRole[] {
  if (Array.isArray(input)) {
    return input;
  }

  if (typeof input === 'object') {
    return input.whitelist ?? defaultWhitelist;
  }

  return [input];
}

function getBlacklist(input: IsAuthorizedArgumentType): UserRole[] {
  return (typeof input === 'object' && !Array.isArray(input)) ? input.blacklist ?? [] : [];
}

/**
 * Authorization route guard factory for Miele MOVE. Check if the user is authorized to access the route. The user needs to have **at least
 * one role from the whitelist** and must **not have any role from the blacklist**.
 *
 * @param [whitelist=[MmuUserRoles.BASIC, MmuUserRoles.ENGINEER, MmuUserRoles.ADMIN]] The roles that are allowed to access the route.
 * @returns {CanActivateFn | CanActivateChildFn}
 *
 * @example ```typescript
 * {
 *   path: 'protected',
 *   component: ProtectedComponent,
 *   canActivate: [checkAuthorization([MmuUserRoles.ADMIN])]
 * }
 * ```
 */
export function isAuthorized(whitelist: UserRole[]): CanActivateFn | CanActivateChildFn;
export function isAuthorized(role: UserRole): CanActivateFn | CanActivateChildFn;
export function isAuthorized(options: Partial<IsAuthorizedGuardOptions>): CanActivateFn | CanActivateChildFn;
export function isAuthorized<O extends IsAuthorizedArgumentType>(options: O): CanActivateFn | CanActivateChildFn;
export function isAuthorized(
  whitelistOrOptions: IsAuthorizedArgumentType = defaultWhitelist
): CanActivateFn | CanActivateChildFn {
  const whitelist = getWhitelist(whitelistOrOptions);
  const blacklist = getBlacklist(whitelistOrOptions);

  return (_, state) => {
    const userService = inject(MmuUserService);
    const userRoles = userService.user.roles ?? [];

    const allowNavigation = whitelist.some(role => userRoles.includes(role)) && !blacklist.some(role => userRoles.includes(role));

    if (!allowNavigation) {
      const logger = inject(MmuLogger);
      logger.warn(`Tried to access a protected page at "${state.url}", but the user is not authorized to. `,
        'Allowed roles:', whitelist,
        'Disallowed roles:', blacklist,
        'User roles:', userRoles
      );
    }

    return allowNavigation;
  };
}
